import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Alert,
  LinearProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import toast from 'react-hot-toast';

import { Iconify } from 'src/components/iconify';
import {
  employeeTransferApi,
  type EmployeeTransferDto,
  type CompanyDto,
  type BranchDto,
} from 'src/api/employee-transfer.api';
import { attendanceApi } from 'src/api/attendance.api';

import { BranchTransferFilterSection } from './BranchTransferFilterSection';
import { BranchEmployeeTable } from './BranchEmployeeTable';


// ----------------------------------------------------------------------

interface BranchEmployeeTransferTabProps {
  branchId: string;
  branchName: string;
}

export default function BranchEmployeeTransferTab({ 
  branchId, 
  branchName 
}: BranchEmployeeTransferTabProps) {
  // State management
  const [employees, setEmployees] = useState<EmployeeTransferDto[]>([]);
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [branches, setBranches] = useState<BranchDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states - branch-specific
  const [searchName, setSearchName] = useState('');
  
  // Individual target branch selection
  const [individualTargetBranches, setIndividualTargetBranches] = useState<
    Record<string, string>
  >({});

  // Transfer loading states
  const [transferLoading, setTransferLoading] = useState<Record<string, boolean>>({});

  // Mid-shift transfer confirmation: holds the transfer awaiting user approval
  const [pendingTransfer, setPendingTransfer] = useState<{
    employeeId: string;
    targetBranchId: string;
  } | null>(null);

    // Filtered employees - memoized for performance
  const filteredEmployees = useMemo(() => 
    employees.filter((employee) => {
      // Branch filter - only show employees from current branch (ID bazlı)
      const branchMatch = employee.branchId === branchId;
      
      // Name filter
      const nameMatch = searchName
        ? `${employee.firstName} ${employee.lastName}`
            .toLowerCase()
            .includes(searchName.toLowerCase())
        : true;

      return branchMatch && nameMatch;
    }), [employees, searchName, branchId]);

  // Available branches for target selection (only same company, excluding current branch)
  const availableTargetBranches = useMemo(() => {
    // Find current branch to get its company
    const currentBranch = branches.find(branch => branch.id === branchId);
    if (!currentBranch) return [];

    // Filter branches to only show branches from the same company
    return branches.filter((branch) => 
      branch.id !== branchId && // Exclude current branch
      branch.companyId === currentBranch.companyId // Only same company
    );
  }, [branches, branchId]);

  // Get current company name for display
  const currentCompanyName = useMemo(() => {
    const currentBranch = branches.find(branch => branch.id === branchId);
    return currentBranch?.companyName || '';
  }, [branches, branchId]);

  // Fetch initial data
  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Branch-specific çalışanları getir
      const response = await employeeTransferApi.getEmployeesForBranchTransfer(branchId, 0, 50, 'firstName', 'asc');
      setEmployees(response.content);
    } catch (err: any) {
      // Fallback: Eğer branch-specific endpoint çalışmazsa, tüm çalışanları getir ve frontend filtreleme yap
      try {
        const fallbackResponse = await employeeTransferApi.getMyEmployees(0, 50, 'firstName', 'asc');
        setEmployees(fallbackResponse.content);
      } catch (fallbackErr: any) {
        const errorMessage = fallbackErr.response?.data?.message || fallbackErr.message || 'Çalışanlar yüklenemedi';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  const fetchCompaniesAndBranches = useCallback(async () => {
    try {
      const companiesData = await employeeTransferApi.getCompanies();
      setCompanies(companiesData);

      // Fetch branches for all companies using Promise.allSettled
      const branchPromises = companiesData.map(async (company) => {
        try {
          return await employeeTransferApi.getBranchesByCompany(company.id);
        } catch (err) {
          return [];
        }
      });

      const branchResults = await Promise.allSettled(branchPromises);
      const allBranches = branchResults
        .filter((result): result is PromiseFulfilledResult<BranchDto[]> => result.status === 'fulfilled')
        .flatMap((result) => result.value);

      setBranches(allBranches);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Şirket/şube verileri yüklenemedi';
      toast.error(errorMessage);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchEmployees();
    fetchCompaniesAndBranches();
  }, [fetchEmployees, fetchCompaniesAndBranches]);

  // Filter handlers
  const handleFilter = useCallback(() => {
    toast.success(`${filteredEmployees.length} çalışan listelendi`);
  }, [filteredEmployees.length]);

  const handleClearFilters = useCallback(() => {
    setSearchName('');
  }, []);



  // Executes the actual transfer (called directly when not mid-shift, or after
  // the user confirms the mid-shift warning dialog).
  const performTransfer = useCallback(
    async (employeeId: string, targetBranchId: string) => {
      try {
        setTransferLoading((prev) => ({ ...prev, [employeeId]: true }));

        await employeeTransferApi.transferEmployee(employeeId, targetBranchId);

        toast.success('Transfer başarıyla tamamlandı');

        // Refresh employee list
        await fetchEmployees();

        // Clear individual target branch selection
        setIndividualTargetBranches((prev) => {
          const updated = { ...prev };
          delete updated[employeeId];
          return updated;
        });
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Transfer başarısız';
        toast.error(errorMessage);
      } finally {
        setTransferLoading((prev) => ({ ...prev, [employeeId]: false }));
      }
    },
    [fetchEmployees]
  );

  // Individual transfer handler — warns first if the employee is mid-shift.
  const handleIndividualTransfer = useCallback(
    async (employeeId: string) => {
      const targetBranchId = individualTargetBranches[employeeId];
      if (!targetBranchId) {
        toast.error('Lütfen hedef şube seçiniz');
        return;
      }

      // Check for an open (ACTIVE) shift before transferring. An open shift has a
      // null checkOutTime — same signal BranchAttendanceTab uses for "Devam ediyor".
      try {
        setTransferLoading((prev) => ({ ...prev, [employeeId]: true }));
        const attendances = await attendanceApi.getEmployeeAttendance(employeeId);
        const hasActiveShift = attendances.some((a) => a.checkOutTime === null);
        if (hasActiveShift) {
          // Defer the transfer until the user confirms the mid-shift warning.
          setPendingTransfer({ employeeId, targetBranchId });
          return;
        }
      } catch (err) {
        // If the attendance check fails, don't block the transfer — fall through.
      } finally {
        setTransferLoading((prev) => ({ ...prev, [employeeId]: false }));
      }

      // Not mid-shift (or the check failed) → transfer immediately.
      await performTransfer(employeeId, targetBranchId);
    },
    [individualTargetBranches, performTransfer]
  );

  // Confirm the mid-shift warning → proceed with the deferred transfer.
  const handleConfirmTransfer = useCallback(async () => {
    if (!pendingTransfer) return;
    const { employeeId, targetBranchId } = pendingTransfer;
    setPendingTransfer(null);
    await performTransfer(employeeId, targetBranchId);
  }, [pendingTransfer, performTransfer]);

  // Cancel the mid-shift warning → abort, no transfer.
  const handleCancelTransfer = useCallback(() => {
    setPendingTransfer(null);
  }, []);

  // Resolve the employee awaiting confirmation (for the dialog copy).
  const pendingEmployee = useMemo(
    () =>
      pendingTransfer
        ? employees.find((employee) => employee.id === pendingTransfer.employeeId)
        : undefined,
    [pendingTransfer, employees]
  );



  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
                 {/* Header */}
         <Stack direction="row" alignItems="center" spacing={1}>
           <Iconify icon="material-symbols:compare-arrows" width={24} height={24} />
           <Typography variant="h6">
             {branchName} - Çalışan Transferi
           </Typography>
           <Chip 
             label={`${filteredEmployees.length} çalışan`} 
             color="primary" 
             size="small" 
           />
           {currentCompanyName && (
             <Chip 
               label={currentCompanyName}
               color="secondary" 
               size="small" 
               variant="outlined"
             />
           )}
         </Stack>

        {/* Branch Info */}
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Iconify icon="mdi:office-building" width={20} height={20} />
              <Typography variant="body2" color="text.secondary">
                Bu şubeden diğer şubelere çalışan transferi yapabilirsiniz
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading Progress */}
        {loading && <LinearProgress />}

        {/* Filter Section */}
        <BranchTransferFilterSection
          searchName={searchName}
          onSearchNameChange={setSearchName}
          branches={branches}
          currentBranchId={branchId}
          currentBranchName={branchName}
          currentCompanyName={currentCompanyName}
          onClearFilters={handleClearFilters}
          disabled={loading}
        />



        <Divider />

        {/* Employee Table */}
        <Card>
          <CardHeader
            title="Transfer Edilecek Çalışanlar"
            subheader={`${branchName} şubesinde ${filteredEmployees.length} çalışan bulunuyor`}
          />
          <CardContent>
            <BranchEmployeeTable
              employees={filteredEmployees}
              availableBranches={availableTargetBranches}
              individualTargetBranches={individualTargetBranches}
              onIndividualTargetBranchChange={setIndividualTargetBranches}
              onIndividualTransfer={handleIndividualTransfer}
              transferLoading={transferLoading}
              disabled={loading}
            />
          </CardContent>
        </Card>
      </Stack>

      {/* Mid-shift transfer warning */}
      <Dialog open={Boolean(pendingTransfer)} onClose={handleCancelTransfer}>
        <DialogTitle>Çalışan şu anda mesaide</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingEmployee
              ? `${pendingEmployee.firstName} ${pendingEmployee.lastName} adlı çalışan şu anda vardiyada (açık mesai). `
              : 'Bu çalışan şu anda vardiyada (açık mesai). '}
            Devam eden vardiyanın saatleri, çalışanın çıkış yaptığı şubeye yazılacaktır.
            Çıkış yapana kadar çalışan her iki şubenin QR kodu ile de çıkış yapabilir.
            Yine de transfer etmek istiyor musunuz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTransfer}>Vazgeç</Button>
          <Button onClick={handleConfirmTransfer} color="primary" variant="contained">
            Yine de Transfer Et
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
